import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import type { DataSourceLink } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/context/ToastContext';
import {
  useDataSourceLinkLoads,
  useStartDataSourceLinkLoad,
} from '@/api/rpc/hooks/core/useDataSources';
import {
  EMPTY_LOAD_SETTINGS,
  MIB,
  parseLoadSettings,
  parsePositive,
  type LoadSettingsInput,
} from './loadSettings';
import { LoadsTable } from './LoadsTable';

/** Per-user column layout key for a link's load history table. */
const LINK_LOAD_HISTORY_TABLE_ID = 'item-master-link-load-history';

interface LinkDialogProps {
  link: DataSourceLink;
  open: boolean;
  onClose: () => void;
}

function linkLabel(link: DataSourceLink): string {
  return `${link.foreignSchemaName}.${link.foreignTableName}`;
}

/**
 * Starts a direct load of the one file a link's pull config selects. Chunk
 * size and parallelism default to the server's settings; blank means "use the
 * default".
 */
export function LinkLoadNowDialog({ link, open, onClose }: LinkDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title: start a direct load of a data source link">
              Load <Var>{linkLabel(link)}</Var> now
            </T>
          </DialogTitle>
        </DialogHeader>
        {open && <LoadNowForm link={link} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

/** Chunk size and parallelism fields, shared by Load now and Load all. */
export function LoadSettingsFields({
  value,
  onChange,
  defaults,
}: {
  value: LoadSettingsInput;
  onChange: (value: LoadSettingsInput) => void;
  defaults: { chunkBytes: number; parallelism: number } | undefined;
}) {
  const chunkInvalid = parsePositive(value.chunkMib, { scale: MIB }) === null;
  const parallelismInvalid =
    parsePositive(value.parallelism, { max: 64 }) === null;
  return (
    <>
      <div className="space-y-1">
        <Label htmlFor="direct-load-chunk-mib">
          <T context="direct load: chunk size field label">Chunk size (MiB)</T>
        </Label>
        <Input
          id="direct-load-chunk-mib"
          type="number"
          min={1}
          value={value.chunkMib}
          aria-invalid={chunkInvalid}
          placeholder={defaults ? String(defaults.chunkBytes / MIB) : undefined}
          onChange={(e) => onChange({ ...value, chunkMib: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="direct-load-parallelism">
          <T context="direct load: parallel chunks field label">Parallelism</T>
        </Label>
        <Input
          id="direct-load-parallelism"
          type="number"
          min={1}
          max={64}
          value={value.parallelism}
          aria-invalid={parallelismInvalid}
          placeholder={defaults ? String(defaults.parallelism) : undefined}
          onChange={(e) => onChange({ ...value, parallelism: e.target.value })}
        />
      </div>
    </>
  );
}

/**
 * The full-load switch, shared by Load now, Load all and the Start Data
 * Source Load step: off, a load leaves ingest rows whose values already match
 * alone and only re-derives the products of the rows that changed.
 */
export function RetransformUnchangedCheckbox({
  checked,
  disabled,
  onCheckedChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="flex cursor-pointer items-center gap-2 font-normal">
        <Checkbox
          checked={checked}
          disabled={disabled}
          onCheckedChange={(v) => onCheckedChange(v === true)}
        />
        <T context="direct load: checkbox to re-transform rows whose values did not change (a full load)">
          Re-transform unchanged rows (full load)
        </T>
      </Label>
      <p className="text-xs text-muted-foreground">
        <T context="help text under the full-load checkbox of a direct load">
          Off, rows whose values already match the ingest table are left alone
          and their products are not re-derived. Turn it on after a mapping
          change, or to repair products a failed load left missing.
        </T>
      </p>
    </div>
  );
}

/**
 * The SQL mode switch, shared like the full-load switch by Load now, Load all
 * and the Start Data Source Load step. Experimental and off by default.
 */
export function SqlModeCheckbox({
  checked,
  disabled,
  onCheckedChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="flex cursor-pointer items-center gap-2 font-normal">
        <Checkbox
          checked={checked}
          disabled={disabled}
          onCheckedChange={(v) => onCheckedChange(v === true)}
        />
        <T context="direct load: checkbox to load the file in SQL mode, an experimental faster path">
          SQL mode (experimental)
        </T>
      </Label>
      <p className="text-xs text-muted-foreground">
        <T context="help text under the SQL mode checkbox of a direct load">
          Merges the whole file in the database and transforms only the rows
          that changed, or every row when the full load is ticked. A link that
          can't use it loads in chunks as usual, and its load history says why.
        </T>
      </p>
    </div>
  );
}

function LoadNowForm({
  link,
  onClose,
}: {
  link: DataSourceLink;
  onClose: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const { data } = useDataSourceLinkLoads(link.id);
  const start = useStartDataSourceLinkLoad();
  const [settings, setSettings] = useState(EMPTY_LOAD_SETTINGS);
  const parsed = parseLoadSettings(settings);

  async function submit() {
    if (parsed.invalid) return;
    try {
      const { loadId } = await start.mutateAsync({
        linkId: link.id,
        chunkBytes: parsed.chunkBytes,
        parallelism: parsed.parallelism,
        skipUnchangedRows: parsed.skipUnchangedRows,
        sqlMode: parsed.sqlMode,
      });
      showToast({
        color: 'success',
        message: gt('Load started: {loadId}', {
          loadId,
          context: 'toast after starting a direct load',
        }),
      });
      onClose();
    } catch (err) {
      showToast({
        color: 'error',
        message:
          err instanceof Error
            ? err.message
            : gt('Failed to start load', { context: 'toast error' }),
      });
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        <T context="direct load dialog description">
          Loads the file the link's pull settings select (filename pattern and
          file selection, which must match exactly one file) into the ingest
          table, then applies the schema's load transform. Leave a field blank
          to use the default.
        </T>
      </p>
      <div className="grid grid-cols-2 gap-3">
        <LoadSettingsFields
          value={settings}
          onChange={setSettings}
          defaults={data?.defaults}
        />
      </div>
      <RetransformUnchangedCheckbox
        checked={settings.retransformUnchanged}
        onCheckedChange={(retransformUnchanged) =>
          setSettings({ ...settings, retransformUnchanged })
        }
      />
      <SqlModeCheckbox
        checked={settings.sqlMode}
        onCheckedChange={(sqlMode) => setSettings({ ...settings, sqlMode })}
      />
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          <T context="cancel direct load button">Cancel</T>
        </Button>
        <Button onClick={submit} disabled={start.isPending || parsed.invalid}>
          <T context="start direct load button">Start load</T>
        </Button>
      </DialogFooter>
    </div>
  );
}

/** A link's recent direct loads; refreshes every 5 s while one is running. */
export function LinkLoadHistoryDialog({
  link,
  open,
  onClose,
}: LinkDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-6xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title: direct load history of a data source link">
              Load history: <Var>{linkLabel(link)}</Var>
            </T>
          </DialogTitle>
        </DialogHeader>
        {open && <LoadHistoryTable linkId={link.id} />}
      </DialogContent>
    </Dialog>
  );
}

function LoadHistoryTable({ linkId }: { linkId: string }) {
  const { data, dataUpdatedAt, isLoading, error } =
    useDataSourceLinkLoads(linkId);

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="loading state for direct load history">Loading loads…</T>
      </p>
    );
  }
  if (error || !data) {
    return (
      <p className="text-sm text-destructive">
        <T context="error state for direct load history">
          Couldn't load this link's load history.
        </T>
      </p>
    );
  }
  if (data.loads.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="empty state for direct load history">
          This link has not been loaded directly yet.
        </T>
      </p>
    );
  }
  // A running load's throughput is measured to the time its rows were
  // fetched, which the 5 s poll keeps fresh; the clock is not read in render.
  return (
    <LoadsTable
      tableId={LINK_LOAD_HISTORY_TABLE_ID}
      loads={data.loads}
      now={dataUpdatedAt}
    />
  );
}
